let instances = [];

class Video {
    /**
     * Creates a new video in an iframe, from a div wrapper.
     *
     * @param {HTMLElement} videoWrapper
     */
    constructor(videoWrapper) {
        const wrapperId = videoWrapper.getAttribute("id");
        const videoType = videoWrapper.getAttribute("data-video-type");
        const videoId = videoWrapper.getAttribute("data-video-id");
        const videoWidth = videoWrapper.getAttribute("data-video-width")
            ? videoWrapper.getAttribute("data-video-width")
            : videoWrapper.offsetWidth;
        const videoHeight = videoWrapper.getAttribute("data-video-height")
            ? videoWrapper.getAttribute("data-video-height")
            : videoWrapper.offsetHeight;
        const videoFullscreen = videoWrapper.getAttribute("data-video-fullscreen") === "true";
        let options = {};

        // Get any extra options and convert into an object
        const videoExtraOptions = videoWrapper.getAttribute("data-video-options");
        let videoExtraOptionsObject;
        if (videoExtraOptions) {
            videoExtraOptionsObject = videoExtraOptions.split("&").reduce((prev, curr) => {
                const parameter = curr.split("=");
                const newPrev = prev;
                newPrev[decodeURIComponent(parameter[0])] = decodeURIComponent(parameter[1]);

                return newPrev;
            }, {});
        }

        const isVideoPlaying = (video) =>
            video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
        const isActiveVideo = (video) => video.closest(".swiper-slide").classList.contains("swiper-slide-active");

        switch (
            videoType // eslint-disable-line default-case
        ) {
            case "vimeo":
                options = {
                    id: videoId,
                    width: videoWidth,
                    height: videoHeight,
                };

                if (typeof videoExtraOptionsObject !== "undefined") {
                    options = Object.assign(options, videoExtraOptionsObject);
                }
                // Create the player
                import("@vimeo/player").then(({ default: Player }) => {
                    window[wrapperId] = new Player(videoWrapper, options);
                    document.dispatchEvent(new CustomEvent(`video_loaded_${wrapperId}`));
                });
                break;
            case "youtube": {
                // YouTube player needs an extra child div to create iframe from so we don't replace wrapper
                const videoDiv = document.createElement("div");

                // Create a play button which will be used to start the video on click. This method is used to prevent
                // loading of YouTube content until the user has interacted with the page.
                const thumbnail = document.createElement("img");
                thumbnail.setAttribute("class", "video-thumbnail");
                thumbnail.setAttribute("src", `https://img.youtube.com/vi/${videoId}/sddefault.jpg`);
                thumbnail.setAttribute("alt", "Video");
                thumbnail.setAttribute("loading", "lazy");

                const playButton = document.createElement("div");
                playButton.setAttribute("class", "video-thumbnail-play");
                playButton.innerHTML = "<div><div></div></div>";

                videoDiv.append(thumbnail, playButton);

                if (typeof videoExtraOptionsObject !== "undefined") {
                    options = Object.assign(options, videoExtraOptionsObject);
                }

                const loadVideo = () => {
                    window[wrapperId] = new window.YT.Player(videoDiv, {
                        width: videoWidth,
                        height: videoHeight,
                        videoId,
                        playerVars: {
                            fs: videoFullscreen ? 1 : 0,
                            rel: 0,
                        },
                        events: {
                            onReady: (event) => {
                                // trigger an event for the tracking module to pick up
                                document.dispatchEvent(new CustomEvent(`video_loaded_${wrapperId}`));
                                event.target.playVideo();
                            },
                        },
                    });
                };

                videoDiv.addEventListener("click", () => {
                    // If the YouTube API is already loaded, load the video
                    if (window.YT) {
                        loadVideo();

                        return;
                    }

                    // Load the script, then the video
                    window.onYouTubeIframeAPIReady = loadVideo;
                    var tag = document.createElement("script");
                    tag.src = "https://www.youtube.com/iframe_api";
                    var firstScriptTag = document.getElementsByTagName("script")[0];
                    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                });

                videoWrapper.appendChild(videoDiv);

                break;
            }
            case "self-hosted": {
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting && isActiveVideo(entry.target) && !isVideoPlaying(entry.target)) {
                            // avoid firing other onplay events on autoplay
                            const existingOnPlay = entry.target.onplay;
                            entry.target.onplay = () => {
                                entry.target.onplay = existingOnPlay;
                            };

                            entry.target.play();
                        } else if (
                            !entry.isIntersecting &&
                            isActiveVideo(entry.target) &&
                            isVideoPlaying(entry.target)
                        ) {
                            // avoid firing other onpause events on autoplay
                            const existingOnPause = entry.target.onpause;
                            entry.target.onpause = () => {
                                entry.target.onpause = existingOnPause;
                            };

                            entry.target.pause();
                        }
                    });
                });

                const videoReady = () => {
                    observer.observe(videoWrapper);
                    document.dispatchEvent(new CustomEvent(`video_loaded_${wrapperId}`));
                };

                const windowOnLoad = () => {
                    const videoPoll = `video_poll_${wrapperId}`;

                    (window[videoPoll] = () => {
                        setTimeout(() => {
                            if (videoWrapper.readyState < 2) {
                                window[videoPoll]();

                                return;
                            }

                            videoReady();
                        }, 500);
                    })();
                };

                window.addEventListener("load", windowOnLoad, { once: true });

                break;
            }
        }
    }
}

export default {
    init: (videoWrapper) => {
        instances.push(new Video(videoWrapper));
    },

    destroy: () => {
        instances.forEach((instance) => instance.unbindEvents());
        instances = [];
    },
};
